import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useMarket } from "@/lib/useMarket";
import { fmtTime } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const browserTz = () => Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
const fmtDate = (d) => d ? new Date(d + "T00:00:00").toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "";

// Ensure the Razorpay Checkout SDK is available; loads it at runtime if the
// index.html tag was blocked/slow. Resolves false if it cannot load.
export function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const existing = document.getElementById("razorpay-sdk");
    if (existing) {
      existing.addEventListener("load", () => resolve(!!window.Razorpay));
      existing.addEventListener("error", () => resolve(false));
      return;
    }
    const s = document.createElement("script");
    s.id = "razorpay-sdk";
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(!!window.Razorpay);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

// Registration card. Currency, price and available batches/sessions/journeys are
// driven entirely by the detected domain market (.in -> India/INR, .com -> Global/USD)
// and by the admin-managed program slot data (no static/hardcoded prices).
export default function MarketRegForm({
  programType,
  id,
  tid = "reg",
  submitTestId = "reg-secure",
  heading = "Join The Live Meditation Enlightenment",
  submitLabel = "Secure My Seat",
  optionHeading = "Choose Your Session",
  optionNote = "",
  optionType = "session", // "session" (single date) | "range" (date range)
}) {
  const nav = useNavigate();
  const { market, countries } = useMarket();
  const [slots, setSlots] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    full_name: "", email: "", country: "India", country_code: "+91",
    timezone: "Asia/Kolkata", mobile: "", consent: true,
  });
  const upd = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const isIndia = market ? market.market !== "com" : true;
  const mk = isIndia ? "in" : "com";

  // Apply market locale defaults (no visible toggle).
  useEffect(() => {
    if (!market) return;
    if (market.market === "com") setForm((s) => ({ ...s, country: "", country_code: "+1", timezone: browserTz() }));
    else setForm((s) => ({ ...s, country: "India", country_code: "+91", timezone: "Asia/Kolkata" }));
  }, [market]);

  // Load active batches/sessions/journeys for this program + market.
  useEffect(() => {
    if (!market) return;
    api.get("/public/programs", { params: { program_type: programType, market: mk } })
      .then(({ data }) => {
        setSlots(data);
        const firstOpen = data.find((s) => !s.is_full);
        setSelectedId(firstOpen ? firstOpen.id : "");
      })
      .catch(() => {});
  }, [market, programType, mk]);

  const pickCountry = (name) => {
    const c = countries.find((x) => x.name === name);
    setForm((s) => ({ ...s, country: name, country_code: c?.dial || s.country_code, timezone: c?.timezone || s.timezone }));
  };

  const optionLabel = (s) => optionType === "range"
    ? `${fmtDate(s.start_date)}${s.end_date ? " – " + fmtDate(s.end_date) : ""} · ${fmtTime(s.start_time)}${s.end_time ? " – " + fmtTime(s.end_time) : ""}`
    : `${fmtDate(s.start_date)} · ${fmtTime(s.start_time)}${s.end_time ? " – " + fmtTime(s.end_time) : ""}`;

  const selected = slots.find((s) => s.id === selectedId);
  const priceStr = selected ? (isIndia ? `₹${selected.price_inr}` : `$${selected.price_usd}`) : "";

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedId) { toast.error("Please select an option before proceeding"); return; }
    if (!form.full_name || !form.email || !form.country || !form.mobile) {
      toast.error("Please complete all mandatory fields"); return;
    }
    setSubmitting(true);
    try {
      const params = new URLSearchParams(window.location.search);
      const { data } = await api.post(`/public/bookings?market=${mk}`, {
        program_type: programType, slot_id: selectedId,
        full_name: form.full_name, email: form.email, mobile: form.mobile,
        country_code: form.country_code, country: form.country, city: "",
        track: isIndia ? "india" : "non_india",
        timezone: form.timezone || browserTz(),
        whatsapp_consent: form.consent, email_consent: form.consent,
        source: params.get("source") || "Website",
        utm_source: params.get("utm_source") || "", utm_campaign: params.get("utm_campaign") || "",
        landing_page: window.location.pathname, referrer: document.referrer || "",
      });
      const bookingId = data.booking_id;
      const { data: route } = await api.get(`/public/payments/route/${bookingId}`);
      // India / INR live → real Razorpay Checkout only. Never fall back to mock here.
      if (route.gateway === "razorpay" && route.mode === "live") {
        const ready = await loadRazorpayScript();
        if (!ready || !window.Razorpay) {
          toast.error("Payment gateway (Razorpay) could not load. Please check your connection and refresh the page, then try again.");
          setSubmitting(false);
          return;
        }
        let order;
        try {
          ({ data: order } = await api.post(`/public/payments/razorpay/create-order/${bookingId}`));
        } catch (e) {
          toast.error("Could not start the payment. Please try again in a moment.");
          setSubmitting(false);
          return;
        }
        const rz = new window.Razorpay({
          key: order.key_id, amount: order.amount, currency: order.currency, order_id: order.order_id,
          name: order.name, description: order.description, prefill: order.prefill, theme: { color: "#E07A5F" },
          handler: async (resp) => {
            try {
              await api.post(`/public/payments/razorpay/verify`, {
                booking_id: bookingId,
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
              });
              nav(`/thank-you/${bookingId}?status=paid`);
            } catch (e) { toast.error("Payment could not be verified. Please contact support."); }
          },
          modal: { ondismiss: () => { setSubmitting(false); toast.message("Payment cancelled"); } },
        });
        rz.on("payment.failed", () => { toast.error("Payment failed. Please try again."); setSubmitting(false); });
        rz.open();
        return;
      }
      // Mock flow — only for gateways not yet live (e.g. .com / PayPal).
      await api.post(`/public/payments/mock-confirm/${bookingId}?status=paid`);
      nav(`/thank-you/${bookingId}?status=paid`);
    } catch (err) { toast.error(formatErr(err)); }
    finally { setSubmitting(false); }
  };

  return (
    <form id={id} onSubmit={submit} className="soft-card" data-testid={`${tid}-form`}>
      <h3 className="font-serif text-2xl">{heading}</h3>
      {selected && <p className="mt-2 text-lg" data-testid={`${tid}-price`}>{priceStr}</p>}

      {/* Batch / session / journey selection */}
      <div className="mt-4 space-y-1">
        <Label>{optionHeading}</Label>
        {optionNote && <p className="text-xs text-[#7E736E] leading-relaxed">{optionNote}</p>}
        <div className="space-y-2" data-testid={`${tid}-options`}>
          {slots.map((s) => (
            <button type="button" key={s.id} disabled={s.is_full}
              onClick={() => !s.is_full && setSelectedId(s.id)}
              data-testid={`${tid}-option-${s.id}`}
              className={`w-full text-left rounded-xl px-4 py-3 border text-sm transition ${selectedId === s.id ? "border-terracotta bg-[#FBF1EC]" : "border-[#EBE5D9]"} ${s.is_full ? "opacity-60 cursor-not-allowed" : "hover:border-terracotta"}`}>
              <div>{optionLabel(s)}</div>
              {s.is_full && <div className="text-xs text-[#8C3A3A] mt-1">This option is full. Please select the next available option.</div>}
            </button>
          ))}
          {slots.length === 0 && <div className="text-sm text-[#7E736E]">No dates available right now. Please check back soon.</div>}
        </div>
      </div>

      <div className="mt-4 space-y-3">
        <div className="space-y-1"><Label>Name*</Label>
          <Input data-testid={`${tid}-name`} value={form.full_name} onChange={(e) => upd("full_name", e.target.value)} required /></div>
        <div className="space-y-1"><Label>Email*</Label>
          <Input data-testid={`${tid}-email`} type="email" value={form.email} onChange={(e) => upd("email", e.target.value)} required /></div>
        <div className="space-y-1">
          <Label>Country*</Label>
          {isIndia ? (
            <Input data-testid={`${tid}-country`} value="India" disabled />
          ) : (
            <Select value={form.country || undefined} onValueChange={pickCountry}>
              <SelectTrigger data-testid={`${tid}-country`}><SelectValue placeholder="Select your country" /></SelectTrigger>
              <SelectContent>
                {countries.filter((c) => c.iso !== "IN").map((c) => (
                  <SelectItem key={c.iso} value={c.name}>{c.name} ({c.dial})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div className="space-y-1"><Label>Code</Label>
            <Input data-testid={`${tid}-code`} value={form.country_code} onChange={(e) => upd("country_code", e.target.value)} /></div>
          <div className="col-span-2 space-y-1"><Label>Mobile Number* (With Country Code for all Countries)</Label>
            <Input data-testid={`${tid}-mobile`} value={form.mobile} onChange={(e) => upd("mobile", e.target.value)} required /></div>
        </div>
        {!isIndia && (
          <p className="text-xs text-[#7E736E]" data-testid={`${tid}-tz`}>Session times will be shared in your timezone: {form.timezone}</p>
        )}
        <label className="flex items-start gap-2 text-xs text-[#5C5955]">
          <Checkbox data-testid={`${tid}-consent`} checked={form.consent} onCheckedChange={(v) => upd("consent", !!v)} />
          <span>I agree to receive emails/what's app from iChaitanya with session details and reminders.</span>
        </label>
      </div>
      <button type="submit" disabled={submitting} data-testid={submitTestId}
        className="btn-primary mt-5 w-full sm:w-auto disabled:opacity-60">
        {submitting ? "Securing…" : `${submitLabel}${priceStr ? " · " + priceStr : ""}`}
      </button>
    </form>
  );
}
